import React, { createContext, useContext, useState, useEffect } from 'react';
import '@/App.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Smartphone, Tablet, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('it');
  return <LanguageContext.Provider value={{ language, setLanguage }}>{children}</LanguageContext.Provider>;
};

// Translations
const translations = {
  en: {
    welcome: 'Verify Your Phone Security',
    subtitle: 'Professional smartphone security assessment by Smart Info',
    chooseLanguage: 'Choose Your Language',
    selectDevice: 'Select Your Device Type',
    ios: 'iOS (iPhone/iPad)',
    android: 'Android',
    other: 'Other',
    next: 'Next',
    back: 'Back',
    question: 'Question',
    of: 'of',
    yes: 'Yes',
    no: 'No',
    showInstructions: 'Show Instructions',
    hideInstructions: 'Hide Instructions',
    yourResults: 'Your Security Assessment Results',
    score: 'Security Issues Found',
    deviceCompromised: 'Device Potentially Compromised',
    deviceSecure: 'Device Appears Secure',
    compromisedDesc: 'Based on your responses, your smartphone may be compromised. We recommend scheduling an appointment with our offices.',
    secureDesc: 'Based on your responses, your smartphone appears to be secure. However, if you want to perform a professional verification, we recommend scheduling an appointment with our offices.',
    ourService: 'Our Analysis Service',
    serviceDesc: 'We will perform verification tests using our TROJAN SOFTWARE SPY ANALYSIS SYSTEM. Through this system, we can analyze the network traffic of any mobile phone or tablet and understand if a trojan-software spy is installed.',
    privacy: 'YOUR DEVICE WILL NOT BE TOUCHED by the operator in complete respect of your privacy.',
    duration: 'The analysis will last approximately 15-20 minutes. At the end of the analysis, a complete PDF report will be generated.',
    bookAppointment: 'Book Appointment & Pay',
    consultationFee: 'Consultation Fee',
    optionalInfo: 'Optional Information',
    email: 'Email',
    name: 'Full Name',
    couponCode: 'Coupon Code',
    applyCoupon: 'Apply',
    proceedPayment: 'Proceed to Payment',
    processing: 'Processing...',
    paymentSuccess: 'Payment Successful!',
    scheduleAppointment: 'Schedule Your Appointment',
    calendlyDesc: 'Select a convenient date and time for your security analysis appointment.',
    thankYou: 'Thank you for choosing Smart Info for your smartphone security analysis.',
    invalidCoupon: 'Invalid or inactive coupon code',
    couponApplied: 'Coupon applied successfully!',
    // Log Analysis
    logAnalysisTitle: 'Security Log Analysis',
    logAnalysisDesc: 'Upload your device diagnostic log to get an AI-powered security analysis.',
    uploadLog: 'Upload Log File',
    selectFile: 'Select File',
    analyzing: 'Analyzing...',
    analysisComplete: 'Analysis Complete',
    riskLevel: 'Risk Level',
    suspiciousItems: 'Suspicious Items Found',
    recommendations: 'Recommendations',
    noIssuesFound: 'No significant security issues were found in your log.',
    skipAnalysis: 'Skip This Step',
    continueToResults: 'Continue to Results',
    lowRisk: 'Low',
    mediumRisk: 'Medium',
    highRisk: 'High',
    criticalRisk: 'Critical',
    uploadInstructions: 'Supported formats: .txt, .log, .json, .xml, .csv, .ips, .crash',
    dragDropFile: 'Drag and drop a file here, or click to select',
    analysisError: 'Error analyzing log file. Please try again.',
  },
  it: {
    welcome: 'Verifica La Sicurezza Del Tuo Telefono',
    subtitle: 'Valutazione professionale della sicurezza dello smartphone di Smart Info',
    chooseLanguage: 'Scegli La Tua Lingua',
    selectDevice: 'Seleziona Il Tipo Di Dispositivo',
    ios: 'iOS (iPhone/iPad)',
    android: 'Android',
    other: 'Altro',
    next: 'Avanti',
    back: 'Indietro',
    question: 'Domanda',
    of: 'di',
    yes: 'Sì',
    no: 'No',
    showInstructions: 'Mostra Istruzioni',
    hideInstructions: 'Nascondi Istruzioni',
    yourResults: 'Risultati Della Valutazione Della Sicurezza',
    score: 'Problemi Di Sicurezza Rilevati',
    deviceCompromised: 'Dispositivo Potenzialmente Compromesso',
    deviceSecure: 'Il Dispositivo Sembra Sicuro',
    compromisedDesc: 'Dalla verifica delle risposte fornite, emerge che il tuo smartphone potrebbe essere compromesso. Ti consigliamo quindi di prenotare un appuntamento presso i nostri uffici.',
    secureDesc: 'Dalla verifica delle risposte fornite, emerge che il tuo smartphone sembrerebbe non essere compromesso. Qualora tu voglia in ogni caso effettuare una verifica professionale, ti consigliamo di prenotare un appuntamento presso i nostri uffici.',
    ourService: 'Il Nostro Servizio Di Analisi',
    serviceDesc: 'Effettueremo alcuni TEST di verifica tramite il nostro SISTEMA di ANALISI TROJAN SOFTWARE SPY. Attraverso questo sistema siamo in grado di analizzare il traffico di rete di qualsiasi cellulare o tablet e comprendere se vi è installato un trojan-software spy.',
    privacy: 'Il device da analizzare NON VIENE TOCCATO dall\'operatore nel completo rispetto della privacy del cliente.',
    duration: 'L\'analisi durerà circa 15-20min. Al termine dell\'analisi verrà generato un Report completo in PDF.',
    bookAppointment: 'Prenota Appuntamento E Paga',
    consultationFee: 'Tariffa Consulenza',
    optionalInfo: 'Informazioni Opzionali',
    email: 'Email',
    name: 'Nome E Cognome',
    couponCode: 'Codice Coupon',
    applyCoupon: 'Applica',
    proceedPayment: 'Procedi Al Pagamento',
    processing: 'Elaborazione...',
    paymentSuccess: 'Pagamento Riuscito!',
    scheduleAppointment: 'Pianifica Il Tuo Appuntamento',
    calendlyDesc: 'Seleziona una data e un\'ora convenienti per il tuo appuntamento di analisi della sicurezza.',
    thankYou: 'Grazie per aver scelto Smart Info per l\'analisi della sicurezza del tuo smartphone.',
    invalidCoupon: 'Codice coupon non valido o inattivo',
    couponApplied: 'Coupon applicato con successo!',
    // Log Analysis
    logAnalysisTitle: 'Analisi Log di Sicurezza',
    logAnalysisDesc: 'Carica il file di log diagnostico del tuo dispositivo per ottenere un\'analisi di sicurezza basata su IA.',
    uploadLog: 'Carica File di Log',
    selectFile: 'Seleziona File',
    analyzing: 'Analisi in corso...',
    analysisComplete: 'Analisi Completata',
    riskLevel: 'Livello di Rischio',
    suspiciousItems: 'Elementi Sospetti Trovati',
    recommendations: 'Raccomandazioni',
    noIssuesFound: 'Non sono stati trovati problemi di sicurezza significativi nel tuo log.',
    skipAnalysis: 'Salta Questo Passaggio',
    continueToResults: 'Continua ai Risultati',
    lowRisk: 'Basso',
    mediumRisk: 'Medio',
    highRisk: 'Alto',
    criticalRisk: 'Critico',
    uploadInstructions: 'Formati supportati: .txt, .log, .json, .xml, .csv, .ips, .crash',
    dragDropFile: 'Trascina qui un file, oppure clicca per selezionarlo',
    analysisError: 'Errore durante l\'analisi del file di log. Riprova.',
  }
};

const t = (key, lang) => translations[lang][key] || key;

// Device Selection Page
const DeviceSelectionPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [deviceType, setDeviceType] = useState('');
  const [showOtherMessage, setShowOtherMessage] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleDeviceSelect = (type) => {
    if (type === 'other') {
      setDeviceType(type);
      setShowOtherMessage(true);
    } else {
      setDeviceType(type);
      setShowOtherMessage(false);
    }
  };

  const handleContinue = () => {
    if (deviceType && deviceType !== 'other') {
      navigate('/assessment', { state: { deviceType } });
    }
  };

  const handleBookConsultation = () => {
    if (settings?.other_device_flow === 'booking') {
      // Go directly to booking
      navigate('/book-appointment', { state: { fromOtherDevice: true } });
    } else {
      // Go to payment page (default)
      navigate('/payment', { state: { fromOtherDevice: true } });
    }
  };

  const goBack = () => {
    if (showOtherMessage) {
      setShowOtherMessage(false);
      setDeviceType('');
    } else {
      navigate('/privacy-consent');
    }
  };

  // Message for "Other" device selection
  const otherDeviceMessage = {
    en: "Your device requires a personalized analysis. Due to the variety of operating systems and configurations available, a specific in-depth assessment with one of our experts is necessary. We invite you to book a consultation to evaluate the security of your smartphone together.",
    it: "Il tuo dispositivo richiede un'analisi personalizzata. Data la varietà di sistemi operativi e configurazioni disponibili, è necessario un approfondimento specifico con un nostro esperto. Ti invitiamo a prenotare una consulenza per valutare insieme la sicurezza del tuo smartphone."
  };

  // If "Other" is selected, show special message
  if (showOtherMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="scan-line" />
        <LanguageSwitcher />
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <Shield className="w-20 h-20 mx-auto mb-6 text-secondary" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {language === 'it' ? 'Consulenza Personalizzata Necessaria' : 'Personalized Consultation Required'}
            </h1>
          </div>

          <div className="glass-panel rounded-sm p-8 md:p-12">
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 text-center">
              {language === 'it' ? otherDeviceMessage.it : otherDeviceMessage.en}
            </p>

            <div className="flex flex-col gap-4">
              <Button
                onClick={handleBookConsultation}
                data-testid="book-consultation-button"
                className="w-full h-14 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono"
              >
                {language === 'it' ? 'Prenota Consulenza' : 'Book Consultation'}
              </Button>
              <Button
                variant="outline"
                onClick={goBack}
                data-testid="back-button"
                className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono"
              >
                {t('back', language)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('selectDevice', language)}</h1>
        </div>

        <div className="glass-panel rounded-sm p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <button
              onClick={() => handleDeviceSelect('ios')}
              data-testid="device-ios-button"
              className={`group relative overflow-hidden bg-zinc-900/50 border transition-all duration-300 p-8 rounded-sm ${
                deviceType === 'ios' ? 'border-primary neon-glow' : 'border-white/5 hover:border-primary/50'
              }`}
            >
              <Smartphone className="w-12 h-12 mx-auto mb-4 text-primary" />
              <p className="text-lg font-mono uppercase tracking-widest text-center">{t('ios', language)}</p>
            </button>

            <button
              onClick={() => handleDeviceSelect('android')}
              data-testid="device-android-button"
              className={`group relative overflow-hidden bg-zinc-900/50 border transition-all duration-300 p-8 rounded-sm ${
                deviceType === 'android' ? 'border-primary neon-glow' : 'border-white/5 hover:border-primary/50'
              }`}
            >
              <Tablet className="w-12 h-12 mx-auto mb-4 text-primary" />
              <p className="text-lg font-mono uppercase tracking-widest text-center">{t('android', language)}</p>
            </button>

            <button
              onClick={() => handleDeviceSelect('other')}
              data-testid="device-other-button"
              className={`group relative overflow-hidden bg-zinc-900/50 border transition-all duration-300 p-8 rounded-sm ${
                deviceType === 'other' ? 'border-primary neon-glow' : 'border-white/5 hover:border-primary/50'
              }`}
            >
              <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
              <p className="text-lg font-mono uppercase tracking-widest text-center">{t('other', language)}</p>
            </button>
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={goBack}
              data-testid="back-button"
              className="flex-1 border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono"
            >
              {t('back', language)}
            </Button>
            <Button
              onClick={handleContinue}
              disabled={!deviceType || deviceType === 'other'}
              data-testid="continue-to-assessment-button"
              className="flex-1 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm disabled:opacity-50"
            >
              {t('next', language)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DeviceSelectionPage, LanguageProvider, useLanguage, t, API };
